import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { apiUrlDynamic, apiUrls } from '../../shared/constants/api.constants';

export interface GetEntryParams {
  timesheetId: number;
  userId?: number;
  projectId?: number;
  date?: string;
  viewType?: string;
}

export interface TimesheetCountResponse {
  billableHours: number;
  nonBillableHours: number;
  leaveCount: number;
  rejectedCount: number;
  ongoingProjectCount: number;
}

export interface TimesheetProject {
  projectId?: number;
  projectCode: string;
  name: string;
}

export interface GetTimesheetsParams {
  user_id: number;
  startDate: string;
  endDate: string;
}

export interface GetTimesheetsResponse {
  success: boolean;
  data: {
    data: any[];
    projects: TimesheetProject[];
  };
  message: string;
}

/** Stage row from GET /project-timesheet/project-list */
export interface ProjectTimesheetListStage {
  id: number;
  phaseName: string;
  description: string;
}

/** Project row from GET /project-timesheet/project-list */
export interface ProjectTimesheetListItem {
  id: number;
  name: string;
  projectCode: string;
  status: string;
  allStages: ProjectTimesheetListStage[];
  /** When present, drives CRM vs Support lists (e.g. RA, SUPPORT). */
  project_type?: string;
}

export interface ProjectTimesheetProjectListResponse {
  success: boolean;
  data: ProjectTimesheetListItem[];
  message: string;
}

function normalizeProjectTimesheetListStage(raw: unknown): ProjectTimesheetListStage | null {
  if (!raw || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const id = Number(rec['id'] ?? rec['stageId'] ?? rec['stage_id'] ?? 0);
  const phaseName = String(rec['phaseName'] ?? rec['phase_name'] ?? rec['stagename'] ?? '').trim();
  if (!Number.isFinite(id) || id <= 0 || !phaseName) return null;
  const description = String(rec['description'] ?? '').trim();
  return { id, phaseName, description };
}

/** Coerce GET /project-timesheet/project-list rows (mixed casing / legacy keys). */
export function normalizeProjectTimesheetListItems(raw: unknown): ProjectTimesheetListItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item): ProjectTimesheetListItem | null => {
      if (!item || typeof item !== 'object') return null;
      const rec = item as Record<string, unknown>;
      const id = Number(rec['id'] ?? rec['projectId'] ?? rec['project_id'] ?? 0);
      const name = String(rec['name'] ?? rec['projectName'] ?? rec['project_name'] ?? '').trim();
      if (!Number.isFinite(id) || id <= 0 || !name) return null;
      const projectCode = String(rec['projectCode'] ?? rec['project_code'] ?? '').trim();
      const status = String(rec['status'] ?? '').trim();
      const projectTypeRaw = rec['project_type'] ?? rec['projectType'];
      const project_type =
        projectTypeRaw != null && String(projectTypeRaw).trim()
          ? String(projectTypeRaw).trim()
          : undefined;
      const stagesRaw = rec['allStages'] ?? rec['all_stages'] ?? rec['allStages'];
      const allStages = Array.isArray(stagesRaw)
        ? stagesRaw
            .map((s) => normalizeProjectTimesheetListStage(s))
            .filter((s): s is ProjectTimesheetListStage => s != null)
        : [];
      return { id, name, projectCode, status, allStages, ...(project_type ? { project_type } : {}) };
    })
    .filter((x): x is ProjectTimesheetListItem => x != null);
}

/** Row from GET /project-timesheet/unreported-entries */
export interface ProjectTimesheetUnreportedEntry {
  id: number;
  userId: number;
  date: string;
  projectId: number | null;
  milestoneId: number | null;
  taskId: number | null;
  taskName: string | null;
  appId: number | null;
  screenHours: number;
  remark: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectTimesheetUnreportedEntriesResponse {
  success: boolean;
  data: ProjectTimesheetUnreportedEntry[];
  message: string;
}

/** Task row inside GET /project-timesheet/timesheet-data `activity.tasks`. */
export interface ProjectTimesheetDataActivityTask {
  task_id?: number;
  /** Some responses use camelCase. */
  taskId?: number;
  logged_hours: number;
  /** Phase / stage label (API may send snake_case, camelCase, or `phase`). */
  stage_name?: string;
  stageName?: string;
  stage?: string;
  phase?: string;
  phase_name?: string;
  phaseName?: string;
  /** Milestone label (API may send snake_case, camelCase, or `milestone`). */
  milestone_name?: string;
  milestoneName?: string;
  milestone?: string;
  task_name?: string;
  comment?: string;
  isBillable?: boolean;
  /** API may send PascalCase / snake_case. */
  IsBillable?: boolean;
  is_billable?: boolean;
  /** Backend: typically `'Billable' | 'Non Billable'`. */
  billingType?: string;
  BillingType?: string;
  billing_type?: string;
}

/** Activity row inside GET /project-timesheet/timesheet-data. */
export interface ProjectTimesheetDataActivity {
  app_id: number | null;
  screen_hours: number;
  tasks: ProjectTimesheetDataActivityTask[];
  activity_type: string;
  activity_title?: string | null;
  activityTitle?: string | null;
  /** When true, activity tasks are already linked to CRM (show Edit vs Map). */
  is_mapped?: boolean;
  /** Backend activity row id (tracked/untracked map-to-CRM). */
  activity_id?: number;
  activityId?: number;
}

/** Project row for one calendar day in GET /project-timesheet/timesheet-data. */
export interface ProjectTimesheetDataProjectDay {
  project_id: number;
  project_name: string;
  project_code: string;
  project_type: string;
  /** e.g. draft | pending | submitted | approved | rejected */
  status?: string;
  activity: ProjectTimesheetDataActivity[];
}

export interface ProjectTimesheetTimesheetDataPayload {
  user_id: number;
  by_date: Record<string, ProjectTimesheetDataProjectDay[]>;
}

export interface ProjectTimesheetTimesheetDataResponse {
  success: boolean;
  data: ProjectTimesheetTimesheetDataPayload;
  message: string;
}

/** Single task from GET /projects/:id/stages/:stageId/tasks */
export interface StageTaskItem {
  taskId: number;
  milestoneId?: number;
  name: string;
  isBillable: boolean;
}

export interface GetStageTasksResponse {
  success: boolean;
  data: StageTaskItem[];
  message: string;
}

/** Row from GET /crm/get-opportunities-lookup */
export interface CrmOpportunityLookupItem {
  id: number;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  /** Linked support project id for pre-sales task fetch / submit. */
  supportProjectId?: number | null;
  supportProjectIds?: number[];
  crmOpportunityId?: string | number | null;
}

export interface CrmOpportunitiesLookupResponse {
  message?: string;
  opportunities?: CrmOpportunityLookupItem[];
}

/** Timesheet task status enum for API and UI. */
export type TimesheetStatus = 'draft' | 'submitted' | 'approved' | 'rejected';

/** Single entry for POST /timesheets */
export interface PostTimesheetEntry {
  task_id: number;
  milestone_id: number;
  stage_id: number;
  source: 'manual' | 'system';
  loggedHours: number;
  screenHours: number;
  comment: string;
}

export interface PostTimesheetPayload {
  date: string;
  remark: string;
  approvalStatus: TimesheetStatus;
  project_id: number;
  user_id: number;
  entries: PostTimesheetEntry[];
  /** When updating an existing timesheet, send its id. */
  timesheetId?: number;
}

/** One line in POST /project-timesheet/timesheet-entries-for-ra-project (CRM tab, RA project). */
export interface ProjectTimesheetEntryForRaItem {
  task_id: number;
  /** Omit for SUPPORT project timesheet lines (support has no RA stage/milestone). */
  milestone_id?: number;
  /** Omit for SUPPORT project timesheet lines. */
  stage_id?: number;
  loggedHours: number;
  source: 'manual' | 'system';
  // screenHours?: number;
  comment: string;
  /** Link CRM task lines to an existing tracked/untracked activity row. */
  activity_id?: number;
  /** e.g. `manual` | `tracked` | `untracked` from timesheet-data activity. */
  activity_type?: string;
}

export interface ProjectTimesheetEntriesForRaPayload {
  date: string;
  project_id: number;
  approvalStatus: TimesheetStatus;
  entries: ProjectTimesheetEntryForRaItem[];
}

/** One allocation line in POST /project-timesheet/reported-entries (single-entry mapping). */
export interface ProjectTimesheetReportedEntryItem {
  projectId: number;
  type: 'RA' | 'SUPPORT';
  screenHours: number;
  /** Optional note when mapping unreported activity to a project. */
  comment?: string;
}

/** Single unreported row mapped with optional hour splits across projects. */
export interface ProjectTimesheetReportedEntriesSinglePayload {
  unreportedEntryId: number;
  reportedEntries: ProjectTimesheetReportedEntryItem[];
}

/** Multiple unreported rows mapped to one project in a single request. */
export interface ProjectTimesheetReportedEntriesBulkPayload {
  unreportedEntryIds: number[];
  projectId: number;
  type: 'RA' | 'SUPPORT';
  /** Optional note when mapping unreported activities to a project. */
  comment?: string;
}

export type ProjectTimesheetReportedEntriesPayload =
  | ProjectTimesheetReportedEntriesSinglePayload
  | ProjectTimesheetReportedEntriesBulkPayload;

/** PATCH /project-timesheet/reported-entries — assign a reported entry to a CRM project. */
export type ProjectTimesheetProjectType = 'RA' | 'SUPPORT';

export interface PatchProjectTimesheetReportedEntryPayload {
  reportedEntryId: number;
  projectId: number;
  type: ProjectTimesheetProjectType;
}

export interface ProjectTimesheetSubmitForApprovalPayload {
  startDate: string;
  endDate: string;
  /** Manage Timesheets: submit on behalf of this users.id (Finance / Org Admin). */
  userId?: number;
}

/** Row from GET /user/get-all-users-basic */
export interface AllUsersBasicApiRow {
  userId: number;
  name: string;
  designation: string;
  profilePhoto?: string | null;
  profileImageUrl?: string | null;
  /** Employment status, e.g. Active / Inactive. */
  status?: string | null;
}

export interface AllUsersBasicResponse {
  message?: string;
  count?: number;
  data?: AllUsersBasicApiRow[] | null;
  success?: boolean;
}

/** Row from GET /project-timesheet/manage/employees */
export interface ManageTimesheetEmployeeApiRow {
  id: string | number;
  name: string;
  role: string;
  initials: string;
  avatarUrl?: string | null;
  imagePath?: string | null;
  profilePic?: string | null;
  profilePhoto?: string | null;
  profileImageUrl?: string | null;
}

export interface ManageTimesheetEmployeesResponse {
  success: boolean;
  message?: string;
  data?: ManageTimesheetEmployeeApiRow[] | null;
}

/** Row from GET /project-timesheet/approval/employees */
export interface TimesheetApprovalEmployeeApiRow {
  id: string | number;
  name: string;
  role?: string;
  designation?: string;
  initials?: string;
  avatarUrl?: string | null;
  imagePath?: string | null;
  profilePic?: string | null;
  profilePhoto?: string | null;
  profileImageUrl?: string | null;
  status?: string | null;
  /** Reporting relationship to the signed-in approver. */
  relation?: 'direct' | 'indirect' | string | null;
  reporteeType?: 'direct' | 'indirect' | string | null;
}

export interface TimesheetApprovalEmployeesBuckets {
  employees?: TimesheetApprovalEmployeeApiRow[];
  direct?: TimesheetApprovalEmployeeApiRow[];
  indirect?: TimesheetApprovalEmployeeApiRow[];
  directReportees?: TimesheetApprovalEmployeeApiRow[];
  indirectReportees?: TimesheetApprovalEmployeeApiRow[];
}

export interface TimesheetApprovalEmployeesResponse {
  success: boolean;
  message?: string;
  data?: TimesheetApprovalEmployeeApiRow[] | TimesheetApprovalEmployeesBuckets | null;
}

/** Line from GET /project-timesheet/manage/week-entries (loose API shape). */
export interface ManageTimesheetEntryApiRow {
  id: string | number;
  employeeId: string | number;
  date: string;
  projectId: string | number;
  projectName: string;
  projectCode?: string | null;
  projectColor?: string | null;
  entryType: 'crm' | 'sup' | string;
  supMode?: 'std' | 'crm' | string | null;
  approvalStatus: string;
  /** CRM project manager users.id — gates Edit / Approve / Reject on Timesheet Approval. */
  projectManagerId?: string | number | null;
  project_manager_id?: string | number | null;
  /** Support reporting manager users.id — gates Approve / Reject on Timesheet Approval. */
  reportingManagerId?: string | number | null;
  reporting_manager_id?: string | number | null;
  stageId?: string | number | null;
  stageName?: string | null;
  milestoneId?: string | number | null;
  milestoneName?: string | null;
  taskId?: string | number | null;
  taskName?: string | null;
  hours: number;
  /** Screen / tracked hours when linked to reported activity. */
  screenHours?: number | null;
  screen_hours?: number | null;
  /**
   * Activity category: `manual` | `tracked` | `untracked`
   * (aliases: reported→tracked, unreported→untracked; also `category`).
   */
  activityType?: string | null;
  activity_type?: string | null;
  category?: string | null;
  /** App / product name for tracked & untracked rows. */
  appLabel?: string | null;
  appName?: string | null;
  app?: string | null;
  /** Mapped activity title (from activity remark). */
  activityTitle?: string | null;
  activity_title?: string | null;
  /** Reserved; may be absent from BE. */
  activityTimeRange?: string | null;
  activity_time_range?: string | null;
  billable?: boolean;
  comment?: string | null;
  createdBy?: string | number | null;
  updatedAt?: string | null;
}

export interface ManageTimesheetWeekEntriesResponse {
  success: boolean;
  message?: string;
  data?: ManageTimesheetEntryApiRow[] | null;
}

export interface ManageTimesheetEntryCreatePayload {
  employeeId: number;
  dates: string[];
  projectId: number;
  projectName?: string;
  projectCode?: string;
  entryType: 'crm' | 'sup';
  supMode?: 'std' | 'crm';
  stageId?: number | null;
  milestoneId?: number | null;
  taskId?: number | null;
  taskName?: string;
  hours: number;
  billable?: boolean;
  comment?: string | null;
}

export interface ManageTimesheetEntryPatchPayload {
  hours?: number;
  comment?: string | null;
  date?: string;
  taskId?: number | null;
  taskName?: string | null;
  /**
   * Approver note. Required (non-empty) on every Timesheet Approval Save PATCH;
   * BE enforces for non-Finance / submitted weeks.
   */
  remark?: string | null;
}

export interface ProjectTimesheetStageMilestoneTasksForUserResponse {
  success: boolean;
  message?: string;
  data?: unknown;
}

export interface ProjectTimesheetRevokeSubmissionPayload {
  /** Target employee (not the Finance/Admin actor). Required by Finance/Admin revoke API. */
  userId: number;
  startDate: string;
  endDate: string;
  /** Omit for all projects in range; include to scope to one project. */
  projectId?: number;
  projectType?: ProjectTimesheetProjectType;
  remark?: string | null;
}

export interface ProjectTimesheetRevokeSubmissionData {
  updatedCount: number;
  timesheetIds: number[];
  trailRowCount: number;
}

export interface ProjectTimesheetRevokeSubmissionResponse {
  success: boolean;
  message?: string;
  data?: ProjectTimesheetRevokeSubmissionData | null;
}

export interface ProjectTimesheetDeleteActivityTaskResponse {
  success: boolean;
  data?: unknown;
  message?: string;
}

export interface ProjectTimesheetDeleteActivityTaskPayload {
  taskIds: number[];
  date: string;
  sourceType?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TimesheetService {
  private period$ = new BehaviorSubject<string>('Daily');
  currentPeriod$ = this.period$.asObservable();

  setPeriod(period: string) {
    this.period$.next(period);
  }
  ModifiedBy: number;
  CreatedBy: number;
  orgId: number;
  constructor(private http: HttpClient, private cookieService: CookieService) {
    this.ModifiedBy = parseInt(this.cookieService.get('userID') || '0', 10) || 0;
    this.CreatedBy = parseInt(this.cookieService.get('userID') || '0', 10) || 0;
    this.orgId = parseInt(this.cookieService.get('orgID') || '0', 10) || 0;
  }

  /** Current user id from cookie (for timesheets API). */
  getCurrentUserId(): number {
    return this.CreatedBy || 0;
  }
  /** GET /projects/:id/stages/:stageId/tasks - tasks for stage dropdown */
  getTaskList(id: number, secondaryId: number | string): Observable<GetStageTasksResponse> {
    const url = apiUrlDynamic(id, secondaryId).resource_allocation.project.get_task_list;
    return this.http.get<GetStageTasksResponse>(url, { responseType: 'json' });
  }

  get_stages(id:number): Observable<any>{
    const url = apiUrlDynamic(id).resource_allocation.project.get_stages;
    return this.http.get(url,{responseType:'json'});
  }

  // Function to get timesheet entry
  getEntry(params: GetEntryParams): Observable<any> {
    const paramsList: string[] = [];
    if(params.timesheetId != null){
      paramsList.push(`timesheetId=${params.timesheetId}`);
    }
    if(params.userId != null){
      paramsList.push(`userId=${params.userId}`);
    }
    if(params.projectId != null){
      paramsList.push(`projectId=${params.projectId}`);
    }
    if(params.date){
      paramsList.push(`date=${params.date}`);
    }
    if(params.viewType){
      paramsList.push(`viewType=${params.viewType}`);
    }
    const query = paramsList.length > 0 ? '?' + paramsList.join('&') : '';
    const url = apiUrls.timesheet.get_entry + query;
    console.log('GET timesheet entry - URL',url);
    return this.http.get(url,{responseType: 'json'});
  }

  submitEntry(payload: any): Observable<any> {
    console.log('Submit timesheet entry - Payload', payload);
    return this.http.post(apiUrls.timesheet.submit_entry, payload, { responseType: 'json' });
  }

  /** POST /timesheets - create or submit timesheet (localUrl). */
  submitTimesheet(payload: PostTimesheetPayload): Observable<{ success: boolean; data?: any; message?: string }> {
    return this.http.post<{ success: boolean; data?: any; message?: string }>(
      apiUrls.timesheet.post_timesheets,
      payload,
      { responseType: 'json', headers: { 'Content-Type': 'application/json' } }
    );
  }

  saveDraft(payload:any): Observable<any>{
    console.log('Save draft - Payload',payload);
    return this.http.put(apiUrls.timesheet.save_draft, payload, {responseType: 'json'});
  }

  getTimesheetData(querry: string): Observable<any> {
    return this.http.get(
      apiUrls.timesheet.get_project_timesheet + querry,
      {
        responseType: 'json',
      }
    );
  }

  getTimesheetdetails(querry: string): Observable<any> {
    return this.http.get(
      apiUrls.timesheet.get_timesheet_data + querry,
      {
        responseType: 'json',
      }
    );
  }
  addTimeSheetLog(querry: any): Observable<any> {
    return this.http.post(
      apiUrls.timesheet.add_time_log, querry,
      {
        responseType: 'json',
      }
    );
  }

  /** GET timesheet summary counts for KPI cards (billable, non-billable, leaves, rejected, ongoing projects). */
  getTimesheetCount(): Observable<{ success: boolean; data: TimesheetCountResponse; message: string }> {
    return this.http.get<{ success: boolean; data: TimesheetCountResponse; message: string }>(
      apiUrls.timesheet.get_timesheet_count,
      { responseType: 'json' }
    );
  }

  /** GET timesheets list for a user and date range. Default range: current month. */
  getTimesheets(params: GetTimesheetsParams): Observable<GetTimesheetsResponse> {
    const query = `?user_id=${params.user_id}&startDate=${params.startDate}&endDate=${params.endDate}`;
    return this.http.get<GetTimesheetsResponse>(
      apiUrls.timesheet.get_timesheets + query,
      { responseType: 'json' }
    );
  }

  /** GET timesheet by id (view/details). Returns full timesheet with project, user, entries. */
  getTimesheetById(id: number): Observable<{ success: boolean; data: any; message?: string }> {
    return this.http.get<{ success: boolean; data: any; message?: string }>(
      `${apiUrls.timesheet.get_timesheets}/${id}`,
      { responseType: 'json' }
    );
  }

  /** GET unreported entries for a user and date. API: GET /api/v1/unreported-entries?user_id=&date= */
  getUnreportedEntries(params: { user_id: number; date: string }): Observable<{ success: boolean; data: any[]; message?: string }> {
    const query = `?user_id=${params.user_id}&date=${encodeURIComponent(params.date)}`;
    return this.http.get<{ success: boolean; data: any[]; message?: string }>(
      apiUrls.timesheet.get_unreported_entries + query,
      { responseType: 'json' }
    );
  }

  /** PATCH timesheet approval status. API: PATCH /api/v1/timesheets/:id/approval-status body: { approvalStatus, remark } */
  patchTimesheetApprovalStatus(
    timesheetId: number,
    body: { approvalStatus: 'approved' | 'rejected'; remark: string }
  ): Observable<{ success: boolean; data?: any; message?: string }> {
    const url = `${apiUrls.timesheet.get_timesheets}/${timesheetId}/approval-status`;
    return this.http.patch<{ success: boolean; data?: any; message?: string }>(url, body, { responseType: 'json' });
  }

  /** GET project list with stages for timesheet Add Entry modal. Query: date (YYYY-MM-DD). */
  getProjectTimesheetProjectList(date: string): Observable<ProjectTimesheetProjectListResponse> {
    const q = `?date=${encodeURIComponent(date)}`;
    return this.http.get<ProjectTimesheetProjectListResponse>(
      apiUrls.timesheet.get_project_timesheet_project_list + q,
      { responseType: 'json' },
    );
  }

  /** GET unreported entries for the signed-in user (project-timesheet). Query: startDate, endDate. */
  getProjectTimesheetUnreportedEntries(
    startDate: string,
    endDate: string,
  ): Observable<ProjectTimesheetUnreportedEntriesResponse> {
    const q = `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
    return this.http.get<ProjectTimesheetUnreportedEntriesResponse>(
      apiUrls.timesheet.get_project_timesheet_unreported_entries + q,
      { responseType: 'json' },
    );
  }

  /** GET /project-timesheet/timesheet-data — projects and tasks logged in range. */
  getProjectTimesheetTimesheetData(
    startDate: string,
    endDate: string,
  ): Observable<ProjectTimesheetTimesheetDataResponse> {
    const q = `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
    return this.http.get<ProjectTimesheetTimesheetDataResponse>(
      apiUrls.timesheet.get_project_timesheet_timesheet_data + q,
      { responseType: 'json' },
    );
  }

  /** POST CRM entry rows for an RA project (not Support). `entries` may contain multiple tasks in one save. */
  postProjectTimesheetEntriesForRa(
    body: ProjectTimesheetEntriesForRaPayload,
  ): Observable<{ success: boolean; data?: unknown; message?: string }> {
    return this.http.post<{ success: boolean; data?: unknown; message?: string }>(
      apiUrls.timesheet.post_project_timesheet_entries_for_ra,
      body,
      { responseType: 'json' },
    );
  }

  /** POST map unreported screen time to one or more projects (RA / SUPPORT split). */
  postProjectTimesheetReportedEntries(
    body: ProjectTimesheetReportedEntriesPayload,
  ): Observable<{ success: boolean; data?: unknown; message?: string }> {
    return this.http.post<{ success: boolean; data?: unknown; message?: string }>(
      apiUrls.timesheet.post_project_timesheet_reported_entries,
      body,
      { responseType: 'json' },
    );
  }

  /** PATCH remap a reported (tracked) entry to a CRM (`RA`) or Support / Pre sales (`SUPPORT`) project. */
  patchProjectTimesheetReportedEntry(
    body: PatchProjectTimesheetReportedEntryPayload,
  ): Observable<{ success: boolean; data?: unknown; message?: string }> {
    return this.http.patch<{ success: boolean; data?: unknown; message?: string }>(
      apiUrls.timesheet.post_project_timesheet_reported_entries,
      body,
      { responseType: 'json' },
    );
  }

  /** POST submit timesheet week for approval. Body: startDate, endDate; optional userId for Manage proxy. */
  postProjectTimesheetSubmitForApproval(
    body: ProjectTimesheetSubmitForApprovalPayload,
  ): Observable<{ success: boolean; data?: unknown; message?: string }> {
    return this.http.post<{ success: boolean; data?: unknown; message?: string }>(
      apiUrls.timesheet.post_project_timesheet_submit_for_approval,
      body,
      { responseType: 'json', headers: { 'Content-Type': 'application/json' } },
    );
  }

  /** POST Finance/Admin revoke of submitted/approved timesheets. Body requires userId + date range. */
  postProjectTimesheetRevokeSubmission(
    body: ProjectTimesheetRevokeSubmissionPayload,
  ): Observable<ProjectTimesheetRevokeSubmissionResponse> {
    return this.http.post<ProjectTimesheetRevokeSubmissionResponse>(
      apiUrls.timesheet.post_project_timesheet_revoke_submission,
      body,
      { responseType: 'json', headers: { 'Content-Type': 'application/json' } },
    );
  }

  /** GET /user/get-all-users-basic — org roster (userId, name, designation, profilePhoto). */
  getAllUsersBasic(): Observable<AllUsersBasicResponse> {
    return this.http.get<AllUsersBasicResponse>(apiUrls.login.get_all_users_basic, {
      responseType: 'json',
    });
  }

  /** GET /project-timesheet/manage/employees — Finance / Org Admin member roster. */
  getManageEmployees(): Observable<ManageTimesheetEmployeesResponse> {
    return this.http.get<ManageTimesheetEmployeesResponse>(apiUrls.timesheet.get_manage_employees, {
      responseType: 'json',
    });
  }

  /**
   * GET /project-timesheet/approval/employees?userId= — direct + indirect reportees
   * for Timesheet Approval. `userId` must match the auth token user (else 403).
   */
  getTimesheetApprovalEmployees(
    userId: number,
  ): Observable<TimesheetApprovalEmployeesResponse> {
    const q = `?userId=${encodeURIComponent(String(userId))}`;
    return this.http.get<TimesheetApprovalEmployeesResponse>(
      apiUrls.timesheet.get_timesheet_approval_employees + q,
      { responseType: 'json' },
    );
  }

  /** GET /project-timesheet/manage/week-entries — Mon–Sun grid for managed users.id. */
  getManageWeekEntries(
    employeeId: number,
    weekStart: string,
    weekEnd: string,
  ): Observable<ManageTimesheetWeekEntriesResponse> {
    const q =
      `?employeeId=${encodeURIComponent(String(employeeId))}` +
      `&weekStart=${encodeURIComponent(weekStart)}` +
      `&weekEnd=${encodeURIComponent(weekEnd)}`;
    return this.http.get<ManageTimesheetWeekEntriesResponse>(
      apiUrls.timesheet.get_manage_week_entries + q,
      { responseType: 'json' },
    );
  }

  /** GET /project-timesheet/manage/project-list?employeeId= — CRM projects for managed member. */
  getManageProjectList(employeeId: number): Observable<ProjectTimesheetProjectListResponse> {
    const q = `?employeeId=${encodeURIComponent(String(employeeId))}`;
    return this.http.get<ProjectTimesheetProjectListResponse>(
      apiUrls.timesheet.get_manage_project_list + q,
      { responseType: 'json' },
    );
  }

  /** GET /project-timesheet/stage-milestone-tasks-for-user?projectId=&userId= */
  getProjectTimesheetStageMilestoneTasksForUser(
    projectId: number,
    userId: number,
  ): Observable<ProjectTimesheetStageMilestoneTasksForUserResponse> {
    const q =
      `?projectId=${encodeURIComponent(String(projectId))}` +
      `&userId=${encodeURIComponent(String(userId))}`;
    return this.http.get<ProjectTimesheetStageMilestoneTasksForUserResponse>(
      apiUrls.timesheet.get_project_timesheet_stage_milestone_tasks_for_user + q,
      { responseType: 'json' },
    );
  }

  /** POST /project-timesheet/manage/entry — create/update hours for managed member. */
  postManageEntry(
    body: ManageTimesheetEntryCreatePayload,
  ): Observable<{ success: boolean; data?: unknown; message?: string }> {
    return this.http.post<{ success: boolean; data?: unknown; message?: string }>(
      apiUrls.timesheet.post_manage_entry,
      body,
      { responseType: 'json', headers: { 'Content-Type': 'application/json' } },
    );
  }

  /** PATCH /project-timesheet/manage/entry/:id */
  patchManageEntry(
    entryId: number | string,
    body: ManageTimesheetEntryPatchPayload,
  ): Observable<{ success: boolean; data?: unknown; message?: string }> {
    return this.http.patch<{ success: boolean; data?: unknown; message?: string }>(
      apiUrls.timesheet.patch_manage_entry(entryId),
      body,
      { responseType: 'json', headers: { 'Content-Type': 'application/json' } },
    );
  }

  /** DELETE /project-timesheet/manage/entry/:id */
  deleteManageEntry(
    entryId: number | string,
  ): Observable<{ success: boolean; data?: unknown; message?: string }> {
    return this.http.delete<{ success: boolean; data?: unknown; message?: string }>(
      apiUrls.timesheet.delete_manage_entry(entryId),
      { responseType: 'json' },
    );
  }

  /** GET /crm/get-opportunities-lookup — id/name list for Support pre-sales opportunity picker. */
  getCrmOpportunitiesLookup(): Observable<CrmOpportunitiesLookupResponse> {
    return this.http.get<CrmOpportunitiesLookupResponse>(
      apiUrls.crm.opportunities.get_opportunities_lookup,
      { responseType: 'json' },
    );
  }

  /** DELETE mapped row by task id + activity id. */
  deleteProjectTimesheetTaskActivity(
    taskId: number,
    activityId?: number,
    payload?: ProjectTimesheetDeleteActivityTaskPayload,
  ): Observable<ProjectTimesheetDeleteActivityTaskResponse> {
    return this.http.delete<ProjectTimesheetDeleteActivityTaskResponse>(
      apiUrls.timesheet.delete_project_timesheet_task_activity(taskId, activityId),
      {
        responseType: 'json',
        ...(payload ? { body: payload } : {}),
      },
    );
  }
}
